import './ClinicalNotePainCompact.css';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clinicalNoteService from './clinicalNoteService.js';
/**
 * Delay before an edit is automatically saved.
 *
 * The delay prevents every individual keystroke from generating
 * a backend request while still keeping the note saved quickly.
 */
const AUTOSAVE_DELAY_MS = 900;
/**
 * Convert a backend numeric value into the string representation
 * required by a controlled HTML input.
 */
function toInputValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return '';
  }
  return String(value);
}
/**
 * Convert a pain input into the Integer/null representation
 * expected by Spring Boot.
 *
 * Blank draft values intentionally become null because incomplete
 * values are allowed while the note remains a draft.
 */
function toNullableInteger(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ''
  ) {
    return null;
  }
  const number =
    Number(value);
  if (
    !Number.isInteger(number)
  ) {
    return null;
  }
  return number;
}
/**
 * Resolve the optimistic-locking version from a clinical-note
 * response.
 *
 * The normal ClinicalNoteResponse exposes entityVersion directly.
 * The nested fallbacks make this component more tolerant if it is
 * later supplied a wrapper response.
 */
function resolveEntityVersion(
  response
) {
  if (!response) {
    return null;
  }
  return (
    response.entityVersion ??
    response.note?.entityVersion ??
    response.clinicalNote?.entityVersion ??
    null
  );
}
/**
 * Resolve the body-part template assigned to the clinical note.
 *
 * A newly created DRAFT may not yet have a saved pain row, so the
 * body-part template ID is needed to load the active PainTemplate.
 */
function resolveBodyPartTemplateId(
  response
) {
  if (!response) {
    return null;
  }
  return (
    response.bodyPartTemplateId ??
    response.note?.bodyPartTemplateId ??
    response.clinicalNote?.bodyPartTemplateId ??
    null
  );
}
/**
 * Perform immediate browser-side validation.
 *
 * The backend remains authoritative. These checks simply prevent
 * clearly invalid values from being repeatedly autosaved while a
 * clinician is typing.
 */
function validatePainForm(
  form,
  painMetadata
) {
  const errors = {};
  const best =
    toNullableInteger(
      form.bestPain
    );
  const current =
    toNullableInteger(
      form.currentPain
    );
  const worst =
    toNullableInteger(
      form.worstPain
    );
  const rawValues = [
    ['bestPain', form.bestPain],
    [
      'currentPain',
      form.currentPain,
    ],
    ['worstPain', form.worstPain],
  ];
  rawValues.forEach(
    ([field, value]) => {
      if (
        value !== '' &&
        !Number.isInteger(
          Number(value)
        )
      ) {
        errors[field] =
          'Pain must be a whole number.';
      }
    }
  );
  const minimum =
    painMetadata?.minimumScore;
  const maximum =
    painMetadata?.maximumScore;
  if (
    Number.isInteger(minimum) &&
    best !== null &&
    best < minimum
  ) {
    errors.bestPain =
      `Best pain cannot be lower than ${minimum}.`;
  }
  if (
    Number.isInteger(maximum) &&
    best !== null &&
    best > maximum
  ) {
    errors.bestPain =
      `Best pain cannot be higher than ${maximum}.`;
  }
  if (
    Number.isInteger(minimum) &&
    current !== null &&
    current < minimum
  ) {
    errors.currentPain =
      `Current pain cannot be lower than ${minimum}.`;
  }
  if (
    Number.isInteger(maximum) &&
    current !== null &&
    current > maximum
  ) {
    errors.currentPain =
      `Current pain cannot be higher than ${maximum}.`;
  }
  if (
    Number.isInteger(minimum) &&
    worst !== null &&
    worst < minimum
  ) {
    errors.worstPain =
      `Worst pain cannot be lower than ${minimum}.`;
  }
  if (
    Number.isInteger(maximum) &&
    worst !== null &&
    worst > maximum
  ) {
    errors.worstPain =
      `Worst pain cannot be higher than ${maximum}.`;
  }
  /*
   * Required clinical relationship:
   *
   * Best <= Current <= Worst
   *
   * Equality is allowed.
   */
  if (
    best !== null &&
    current !== null &&
    current < best
  ) {
    errors.currentPain =
      'Current pain cannot be lower than Best pain.';
  }
  if (
    current !== null &&
    worst !== null &&
    current > worst
  ) {
    errors.currentPain =
      'Current pain cannot be higher than Worst pain.';
  }
  if (
    best !== null &&
    worst !== null &&
    best > worst
  ) {
    errors.bestPain =
      'Best pain cannot be higher than Worst pain.';
  }
  return errors;
}
/**
 * Individual numeric pain field.
 */
function PainInput({
  id,
  label,
  value,
  required,
  minimum,
  maximum,
  error,
  disabled,
  onChange,
}) {
  return (
    <div
      style={{
        width: '100%',
        minWidth: 0,
      }}
    >
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontWeight: 700,
          fontSize: 13,
          marginBottom: 4,
          color: '#222',
        }}
      >
        {label}
        {required && (
          <span
            aria-hidden="true"
            style={{
              marginLeft: 4,
              color: '#b42318',
            }}
          >
            *
          </span>
        )}
      </label>
      <input
        id={id}
        type="number"
        step="1"
        value={value}
        min={
          Number.isInteger(
            minimum
          )
            ? minimum
            : undefined
        }
        max={
          Number.isInteger(
            maximum
          )
            ? maximum
            : undefined
        }
        disabled={disabled}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        aria-invalid={
          Boolean(error)
        }
        aria-describedby={
          error
            ? `${id}-error`
            : undefined
        }
        style={{
          width: '100%',
          boxSizing:
            'border-box',
          minHeight: 38,
          border:
            error
              ? '1px solid #b42318'
              : '1px solid #bbb',
          borderRadius: 6,
          padding:
            '8px 10px',
          fontSize: 15,
          background:
            disabled
              ? '#f3f4f6'
              : '#fff',
        }}
      />
      {error && (
        <div
          id={`${id}-error`}
          role="alert"
          style={{
            marginTop: 5,
            fontSize: 12,
            color: '#b42318',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
/**
 * Editable Pain section shared by the clinical-note types.
 *
 * The component:
 *
 * - loads existing pain documentation
 * - loads the current optimistic-locking version
 * - allows incomplete draft values
 * - validates Best <= Current <= Worst
 * - autosaves after a short delay
 * - refreshes the parent note version after every successful save
 * - serializes saves so two autosaves cannot use the same version
 * - displays repetition warnings returned by the backend
 */
export default function ClinicalNotePainSection({
  clinicalNoteId,
  disabled = false,
  onSaved,
  onVersionChange,
  navigationCoordinator,
}) {
  const [warningDetailsOpen, setWarningDetailsOpen] = useState(false);
  const [
    form,
    setForm,
  ] = useState({
    bestPain: '',
    currentPain: '',
    worstPain: '',
    painComments: 'Sharp',
  });
  const [
    painMetadata,
    setPainMetadata,
  ] = useState(null);
  const [
    loading,
    setLoading,
  ] = useState(true);
  const [
    loadError,
    setLoadError,
  ] = useState('');
  const [
    saveError,
    setSaveError,
  ] = useState('');
  const [
    saveStatus,
    setSaveStatus,
  ] = useState('saved');
  const [
    editRevision,
    setEditRevision,
  ] = useState(0);
  const [
    initialized,
    setInitialized,
  ] = useState(false);
  /*
   * Refs are used by the autosave pipeline so it always sees the
   * newest values without creating overlapping save requests.
   */
  const formRef =
    useRef(form);
  const versionRef =
    useRef(null);
  const revisionRef =
    useRef(0);
  const savedRevisionRef =
    useRef(0);
  const savingRef =
    useRef(false);
  const mountedRef =
    useRef(true);
  const flushSaveRef =
    useRef(null);
  // A promise, rather than a boolean alone, lets navigation await active saves.
  const inFlightSaveRef = useRef(null);
  const failedRevisionRef = useRef(null);
  const initializedRef = useRef(false);
  const painMetadataRef = useRef(null);
  const loadErrorRef = useRef('');
  const activeNoteIdRef = useRef(String(clinicalNoteId ?? ''));

  useEffect(() => {
    painMetadataRef.current = painMetadata;
  }, [painMetadata]);
  useEffect(() => {
    loadErrorRef.current = loadError;
  }, [loadError]);
  useEffect(() => {
    formRef.current =
      form;
  }, [form]);
  useEffect(() => {
    return () => {
      mountedRef.current =
        false;
    };
  }, []);
  /**
   * Reset and load this section whenever the clinical-note ID changes.
   *
   * Existing Pain:
   *
   * - use the PainResponse returned by the saved patient record
   * - keep the historical template metadata associated with that row
   *
   * Brand-new DRAFT:
   *
   * - ClinicalNotePainMain does not exist yet
   * - load the active PainTemplate for the note's body part
   * - render an empty form using that template configuration
   * - allow the first autosave to create ClinicalNotePainMain
   */
  useEffect(() => {
    let cancelled = false;
    async function loadPainSection() {
      setLoading(true);
      setInitialized(false);
      initializedRef.current = false;
      activeNoteIdRef.current = String(clinicalNoteId ?? '');
      setLoadError('');
      setSaveError('');
      setSaveStatus('saved');
      setWarningDetailsOpen(false);
      revisionRef.current = 0;
      savedRevisionRef.current = 0;
      // The parent must finish navigation saves before switching IDs.
      // Do not reset the in-flight promise here: a stale request must still
      // be awaited by the coordinator for the note it belongs to.
      failedRevisionRef.current = null;
      try {
        /*
         * Load the saved patient Pain record and parent clinical note
         * together.
         *
         * A new DRAFT is expected to return null for getPain().
         */
        const [
          painResponse,
          noteResponse,
        ] = await Promise.all([
          clinicalNoteService.getPain(
            clinicalNoteId
          ),
          clinicalNoteService.getClinicalNote(
            clinicalNoteId
          ),
        ]);
        if (cancelled) {
          return;
        }
        /*
         * Every editable section needs the parent note's current
         * optimistic-locking version.
         */
        const version = resolveEntityVersion(
          noteResponse
        );
        if (
          version === null ||
          version === undefined
        ) {
          throw new Error(
            'The clinical note version could not be loaded.'
          );
        }
        versionRef.current = version;
        /*
         * Existing saved Pain documentation.
         *
         * PainResponse already contains the template metadata that
         * belongs to this saved clinical record, so do not substitute
         * the currently active template.
         */
        if (painResponse) {
          setPainMetadata(
            painResponse
          );
          const loadedForm = {
            bestPain: toInputValue(
              painResponse.bestPain
            ),
            currentPain: toInputValue(
              painResponse.currentPain
            ),
            worstPain: toInputValue(
              painResponse.worstPain
            ),
            painComments:
              painResponse.painComments ?? '',
          };
          formRef.current = loadedForm;
          setForm(
            loadedForm
          );
        } else {
          /*
           * Brand-new DRAFT.
           *
           * No ClinicalNotePainMain row exists yet. Resolve the active
           * PainTemplate from the body part already snapshotted onto
           * ClinicalNoteMain.
           */
          const bodyPartTemplateId =
            resolveBodyPartTemplateId(
              noteResponse
            );
          if (
            bodyPartTemplateId === null ||
            bodyPartTemplateId === undefined
          ) {
            throw new Error(
              'The clinical note does not have a body-part template.'
            );
          }
          const painTemplate =
            await clinicalNoteService
              .getActivePainTemplate(
                bodyPartTemplateId
              );
          if (cancelled) {
            return;
          }
          if (
            !painTemplate ||
            typeof painTemplate !== 'object'
          ) {
            throw new Error(
              'The active pain template could not be loaded.'
            );
          }
          /*
           * Use the template only as metadata.
           *
           * Do NOT create an empty patient Pain row here. The backend
           * will create ClinicalNotePainMain on the first successful
           * autosave.
           */
          setPainMetadata(
            painTemplate
          );
          const emptyForm = {
            bestPain: '',
            currentPain: '',
            worstPain: '',
            painComments: 'Sharp',
          };
          formRef.current = emptyForm;
          setForm(
            emptyForm
          );
        }
        initializedRef.current = true;
        setInitialized(true);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setLoadError(
          error?.message ||
            'Pain information could not be loaded.'
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    if (clinicalNoteId) {
      loadPainSection();
    } else {
      setLoadError(
        'Clinical note ID is required.'
      );
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [clinicalNoteId]);
  const validationErrors =
    useMemo(
      () =>
        validatePainForm(
          form,
          painMetadata
        ),
      [form, painMetadata]
    );
  const hasValidationErrors =
    Object.keys(
      validationErrors
    ).length > 0;
  /**
   * Mark one field as changed.
   *
   * No network request is made immediately. The autosave timer below
   * waits until the clinician pauses editing.
   */
  function updateField(
    field,
    value
  ) {
    if (disabled) {
      return;
    }
    setForm((current) => {
      const next = {
        ...current,
        [field]: value,
      };
      formRef.current =
        next;
      return next;
    });
    revisionRef.current += 1;
    setEditRevision(
      revisionRef.current
    );
    failedRevisionRef.current = null;
    setSaveError('');
    setSaveStatus('unsaved');
  }
  /**
   * Refresh the parent clinical-note version after a successful
   * section save.
   *
   * PainResponse intentionally represents pain data; the current
   * optimistic-locking version belongs to the parent note.
   */
  const refreshEntityVersion =
    useCallback(
      async () => {
        const noteResponse =
          await clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            );
        const newVersion =
          resolveEntityVersion(
            noteResponse
          );
        if (
          newVersion === null ||
          newVersion ===
            undefined
        ) {
          throw new Error(
            'Pain was saved, but the updated clinical note version could not be loaded.'
          );
        }
        versionRef.current =
          newVersion;
        if (
          typeof onVersionChange ===
          'function'
        ) {
          onVersionChange(
            newVersion,
            noteResponse
          );
        }
        return noteResponse;
      },
      [
        clinicalNoteId,
        onVersionChange,
      ]
    );
  /**
   * Save any pending revisions, awaiting the active request if one exists.
   *
   * The same operation is used by the debounce and by the navigator.
   * A failed request is not automatically retried indefinitely; a new
   * edit or an explicit Retry Save is required.
   */
  const flushSave = useCallback(async (forceRetry = false) => {
    if (inFlightSaveRef.current) {
      await inFlightSaveRef.current;
    }
    if (disabled) return;
    if (!initializedRef.current) {
      throw new Error('Pain is still loading. Try switching visits after it loads.');
    }
    if (loadErrorRef.current) throw new Error(loadErrorRef.current);

    const run = async () => {
      while (revisionRef.current > savedRevisionRef.current) {
        const revisionBeingSaved = revisionRef.current;
        if (!forceRetry && failedRevisionRef.current === revisionBeingSaved) {
          throw new Error('Pain autosave failed. Use Retry Save before changing visits.');
        }
        forceRetry = false;
        const currentForm = formRef.current;
        const errors = validatePainForm(currentForm, painMetadataRef.current);
        if (Object.keys(errors).length > 0) {
          setSaveStatus('invalid');
          throw new Error('Correct the invalid Pain values before switching visits.');
        }

        savingRef.current = true;
        setSaveStatus('saving');
        setSaveError('');
        try {
          // Other sections can autosave after Pain initially loads. Fetch the
          // authoritative parent version immediately before every mutation.
          // The backend still rejects genuine concurrent edits via expectedVersion.
          await refreshEntityVersion();
          const response = await clinicalNoteService.savePain(clinicalNoteId, {
            expectedVersion: versionRef.current,
            bestPain: toNullableInteger(currentForm.bestPain),
            currentPain: toNullableInteger(currentForm.currentPain),
            worstPain: toNullableInteger(currentForm.worstPain),
            painComments: currentForm.painComments.trim() || null,
          });
          if (!mountedRef.current ||
              activeNoteIdRef.current !== String(clinicalNoteId)) {
            throw new Error('Pain editor changed while a save was running.');
          }
          setPainMetadata(response);
          painMetadataRef.current = response;
          const noteResponse = await refreshEntityVersion();
          if (!mountedRef.current ||
              activeNoteIdRef.current !== String(clinicalNoteId)) {
            throw new Error('Pain editor changed while a save was running.');
          }
          savedRevisionRef.current = revisionBeingSaved;
          failedRevisionRef.current = null;
          if (typeof onSaved === 'function') onSaved(response, noteResponse);
          setSaveStatus(revisionRef.current > savedRevisionRef.current ? 'unsaved' : 'saved');
        } catch (error) {
          failedRevisionRef.current = revisionBeingSaved;
          const message = error?.message || 'Pain information could not be autosaved.';
          if (mountedRef.current &&
              activeNoteIdRef.current === String(clinicalNoteId)) {
            setSaveStatus('error');
            setSaveError(message);
          }
          throw new Error(message);
        } finally {
          savingRef.current = false;
        }
      }
    };

    const promise = run();
    inFlightSaveRef.current = promise;
    try {
      await promise;
    } finally {
      if (inFlightSaveRef.current === promise) inFlightSaveRef.current = null;
    }
  }, [clinicalNoteId, disabled, onSaved, refreshEntityVersion]);

  useEffect(() => {
    flushSaveRef.current = flushSave;
  }, [flushSave]);

  /**
   * Register the Pain editor with the visit-navigation save barrier.
   * The parent owns navigation; the editor only reports its save state.
   */
  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'pain', {
      flush: () => flushSaveRef.current?.(),
      getState: () => {
        const dirty = revisionRef.current > savedRevisionRef.current;
        return {
          dirty,
          saving: Boolean(inFlightSaveRef.current),
          invalid: dirty && Object.keys(
            validatePainForm(formRef.current, painMetadataRef.current)
          ).length > 0,
          error: dirty && failedRevisionRef.current === revisionRef.current
            ? 'Pain autosave failed. Use Retry Save or edit the field before switching visits.'
            : (loadErrorRef.current || ''),
        };
      },
    });
  }, [navigationCoordinator, clinicalNoteId]);

  /** Allow deliberate retry after a save failure. */
  function retrySave() {
    failedRevisionRef.current = null;
    setSaveError('');
    void flushSaveRef.current?.(true).catch(() => {});
  }

  /**
   * Debounced autosave.
   */
  useEffect(() => {
    if (
      !initialized ||
      disabled ||
      editRevision <=
        savedRevisionRef.current
    ) {
      return undefined;
    }
    if (hasValidationErrors) {
      setSaveStatus(
        'invalid'
      );
      return undefined;
    }
    if (failedRevisionRef.current === revisionRef.current) {
      return undefined;
    }
    const timer =
      window.setTimeout(
        () => {
          flushSaveRef
            .current?.().catch(() => {});
        },
        AUTOSAVE_DELAY_MS
      );
    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    editRevision,
    initialized,
    disabled,
    hasValidationErrors,
  ]);
  /**
   * Text shown in the autosave status area.
   */
  const saveStatusText =
    useMemo(() => {
      switch (saveStatus) {
        case 'saving':
          return 'Saving…';
        case 'unsaved':
          return 'Unsaved changes';
        case 'invalid':
          return 'Waiting for valid values';
        case 'error':
          return 'Autosave failed';
        default:
          return 'Saved';
      }
    }, [saveStatus]);
  const minimum =
    painMetadata
      ?.minimumScore;
  const maximum =
    painMetadata
      ?.maximumScore;
  const rangeText =
    Number.isInteger(
      minimum
    ) &&
    Number.isInteger(
      maximum
    )
      ? `${minimum}–${maximum}`
      : null;
  if (loading) {
    return (
      <section
        style={{
          border:
            '1px solid #ddd',
          borderRadius: 10,
          padding: 16,
          background: '#fff',
        }}
      >
        Loading pain section…
      </section>
    );
  }
  if (loadError) {
    return (
      <section
        style={{
          border:
            '1px solid #c62828',
          borderRadius: 10,
          padding: 16,
          background:
            '#fff5f5',
        }}
      >
        <strong>
          Pain section could not be loaded
        </strong>
        <p
          style={{
            marginBottom: 0,
          }}
        >
          {loadError}
        </p>
      </section>
    );
  }
  return (
    <section
      className="clinical-note-pain-compact"
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* ===================================================
          SECTION HEADER
          =================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding: '10px 12px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            {painMetadata
              ?.displayName ||
              'Pain Scale'}
          </h3>
          {rangeText && (
            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                color: '#666',
              }}
            >
              Scale: {rangeText}
            </div>
          )}
        </div>
        <div
          aria-live="polite"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color:
              saveStatus ===
              'error'
                ? '#b42318'
                : saveStatus ===
                    'invalid'
                  ? '#9a6700'
                  : saveStatus ===
                      'saving'
                    ? '#555'
                    : '#2e6a35',
          }}
        >
          {disabled
            ? 'Read only'
            : saveStatusText}
        </div>
      </div>
      <div
        style={{
          padding: 12,
        }}
      >
        {/* =================================================
            PAIN VALUES
            ================================================= */}
        <div
          style={{
            display: 'grid',
            gap: 10,
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          }}
        >
          <PainInput
            id="clinical-note-best-pain"
            label="Best"
            value={
              form.bestPain
            }
            required={Boolean(
              painMetadata
                ?.bestRequired
            )}
            minimum={minimum}
            maximum={maximum}
            error={
              validationErrors
                .bestPain
            }
            disabled={disabled}
            onChange={(value) =>
              updateField(
                'bestPain',
                value
              )
            }
          />
          <PainInput
            id="clinical-note-current-pain"
            label="Current"
            value={
              form.currentPain
            }
            required={Boolean(
              painMetadata
                ?.currentRequired
            )}
            minimum={minimum}
            maximum={maximum}
            error={
              validationErrors
                .currentPain
            }
            disabled={disabled}
            onChange={(value) =>
              updateField(
                'currentPain',
                value
              )
            }
          />
          <PainInput
            id="clinical-note-worst-pain"
            label="Worst"
            value={
              form.worstPain
            }
            required={Boolean(
              painMetadata
                ?.worstRequired
            )}
            minimum={minimum}
            maximum={maximum}
            error={
              validationErrors
                .worstPain
            }
            disabled={disabled}
            onChange={(value) =>
              updateField(
                'worstPain',
                value
              )
            }
          />
        </div>
        <div className="clinical-note-pain-compact__hint">
          Best ≤ Current ≤ Worst. Equal values are allowed.
        </div>
        {/* =================================================
            COMMENTS
            ================================================= */}
        <div
          style={{
            marginTop: 10,
          }}
        >
          <label
            htmlFor="clinical-note-pain-comments"
            style={{
              display: 'block',
              fontWeight: 700,
              fontSize: 14,
              marginBottom: 6,
            }}
          >
            Pain Comments
          </label>
          <textarea
            id="clinical-note-pain-comments"
            value={
              form.painComments
            }
            disabled={disabled}
            rows={2}
            onChange={(event) =>
              updateField(
                'painComments',
                event.target.value
              )
            }
            placeholder="Enter patient-specific pain comments when applicable."
            style={{
              width: '100%',
              boxSizing:
                'border-box',
              resize: 'vertical',
              border:
                '1px solid #bbb',
              borderRadius: 6,
              padding: 10,
              fontSize: 14,
              lineHeight: 1.45,
              minHeight: 64,
              background:
                disabled
                  ? '#f3f4f6'
                  : '#fff',
            }}
          />
        </div>
        {/* =================================================
            BACKEND WARNING
            ================================================= */}
        {painMetadata?.repetitionWarning && (
          <div
            className={`clinical-note-pain-compact__warning ${painMetadata.warningAcknowledged ? 'is-acknowledged' : ''}`}
            role={painMetadata.warningAcknowledged ? 'status' : 'alert'}
          >
            <button
              type="button"
              className="clinical-note-pain-compact__warning-toggle"
              aria-expanded={warningDetailsOpen}
              onClick={() => setWarningDetailsOpen((open) => !open)}
            >
              <span>
                {painMetadata.warningAcknowledged ? '✓' : '⚠'}{' '}
                Pain repetition warning
                {painMetadata.warningAcknowledged ? ' — acknowledged' : ' — review required'}
              </span>
              <span aria-hidden="true">{warningDetailsOpen ? '−' : '+'}</span>
            </button>
            {warningDetailsOpen && (
              <div className="clinical-note-pain-compact__warning-detail">
                Pain values have remained unchanged across the configured
                number of previous notes.{' '}
                {painMetadata.warningAcknowledged
                  ? 'This warning has been acknowledged.'
                  : 'Review the recorded measurements. Correct an error, or use the central warning panel to acknowledge a clinically accurate result.'}
              </div>
            )}
          </div>
        )}
        {/* =================================================
            SAVE ERROR
            ================================================= */}
        {saveError && (
          <div
            role="alert"
            style={{
              marginTop: 16,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 12,
              color: '#8a1c13',
            }}
          >
            <strong>
              Pain was not saved.
            </strong>
            <div
              style={{
                marginTop: 4,
              }}
            >
              {saveError}
            </div>
            {!disabled && (
              <button type="button" onClick={retrySave} style={{ marginTop: 10 }}>
                Retry Save
              </button>
            )}
          </div>
        )}
        {/* =================================================
            READ-ONLY MESSAGE
            ================================================= */}
        {disabled && (
          <div
            style={{
              marginTop: 14,
              fontSize: 13,
              color: '#666',
            }}
          >
            This section cannot be
            edited because the
            clinical note is
            currently read only or
            locked.
          </div>
        )}
      </div>
    </section>
  );
}
